import React, { useState, useEffect, useRef, useContext } from "react";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import AuthService from "../../../api/services/AuthService";
import { ProfileContext } from "../../../context/ProfileProvider";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import moment from "moment";

/** Backend config may expose type code under different property names. */
const getKycDocumentCode = (doc) => {
    if (!doc || typeof doc !== "object") return "";
    const code = doc.code ?? doc.document_type ?? doc.documentType ?? doc.type ?? doc.id;
    return code != null ? String(code).trim() : "";
};

const KycPage = (props) => {
    const { handleUserDetails } = useContext(ProfileContext);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const selfieInputRef = useRef(null);

    // =========================================================================
    // STATE - User Details
    // =========================================================================
    const [emailId, setEmailId] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [kycVerfied, setKycVerfied] = useState("");
    const [reason, setReason] = useState("");
    const [documentsToResubmit, setDocumentsToResubmit] = useState([]);
    const [needsResubmission, setNeedsResubmission] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // Submission in progress flag

    // Individual document statuses from backend
    const [idDocStatus, setIdDocStatus] = useState(null);
    const [taxDocStatus, setTaxDocStatus] = useState(null);
    const [selfieStatus, setSelfieStatus] = useState(null);

    // Document types user submitted (e.g., "AADHAAR", "PAN", "PASSPORT")
    const [submittedIdDocType, setSubmittedIdDocType] = useState(null);
    const [submittedTaxDocType, setSubmittedTaxDocType] = useState(null);

    // Existing document numbers for resubmission (user can edit)
    const [existingIdDocNumber, setExistingIdDocNumber] = useState('');
    const [existingTaxDocNumber, setExistingTaxDocNumber] = useState('');
    const [existingCountryCode, setExistingCountryCode] = useState('');

    // Resubmission form state for document numbers
    const [resubmitIdNumber, setResubmitIdNumber] = useState('');
    const [resubmitTaxNumber, setResubmitTaxNumber] = useState('');

    // =========================================================================
    // STATE - Countries and KYC Config from Backend
    // =========================================================================
    const [countries, setCountries] = useState([]);
    const [kycConfig, setKycConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(false);

    // =========================================================================
    // STATE - Modal Form Fields
    // =========================================================================
    const [modalStep, setModalStep] = useState(0); // For main KYC modal
    const [resubmitStep, setResubmitStep] = useState(0); // For resubmit modal - separate state
    const [modalTitle, setModalTitle] = useState("Select Country and ID Type"); // Modal title state
    const [modalCountry, setModalCountry] = useState("");
    const [modalIdType, setModalIdType] = useState("");
    const [modalTaxType, setModalTaxType] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [infoDob, setInfoDob] = useState("");
    const [gender, setGender] = useState("male");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [infoState, setInfoState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [aadhar, setAadhar] = useState(""); // ID document number
    const [panCard, setPanCard] = useState(""); // Tax document number

    // =========================================================================
    // STATE - File Uploads
    // =========================================================================
    const [localFront, setLocalFront] = useState("");
    const [localBack, setLocalBack] = useState("");
    const [localPanCard, setLocalPanCard] = useState("");
    const [localSelfie, setLocalSelfie] = useState("");
    const [previewImages, setPreviewImages] = useState({ selfie: "", doc_front: "", doc_back: "", pan: "" });

    // =========================================================================
    // STATE - Camera / Selfie
    // =========================================================================
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [cameraError, setCameraError] = useState("");
    const [selfieCaptured, setSelfieCaptured] = useState(false);

    // =========================================================================
    // STATE - Validation Errors
    // =========================================================================
    const [documentNumberError, setDocumentNumberError] = useState("");
    const [taxDocumentError, setTaxDocumentError] = useState("");

    // =========================================================================
    // STATE - FAQ
    // =========================================================================
    const [activeIndex, setActiveIndex] = useState(null);

    // =========================================================================
    // EFFECTS
    // =========================================================================

    // Initialize from props
    useEffect(() => {
        if (props?.userDetails) {
            const user = props.userDetails;
            setEmailId(user.emailId || "");
            // Store mobile number with country code (same as TwofactorPage)
            setMobileNumber(`${user.country_code || ''} ${user.mobileNumber || ''}`.trim());
            setKycVerfied(user.kycVerified);
            setReason(user.kyc_reject_reason || "");
            setFirstName(user.firstName || "");
            setLastName(user.lastName || "");
        }
    }, [props?.userDetails]);

    // Fetch countries on mount
    useEffect(() => {
        fetchCountries();
        fetchKycStatus();
    }, []);

    // Cleanup camera on unmount
    useEffect(() => {
        return () => stopCamera();
    }, []);

    // Fetch KYC config when country changes
    useEffect(() => {
        if (modalCountry) {
            fetchKycConfig(modalCountry);
        } else {
            setKycConfig(null);
            setModalIdType("");
            setModalTaxType("");
        }
    }, [modalCountry]);

    // Update modal title when modalStep changes
    useEffect(() => {
        const stepTitles = [
            'Select Country and ID Type',
            'Personal Details',
            'Take a Photo of Your ID Card',
            'Income Tax & Selfie',
            'Face Verification',
            'Review Your Information'
        ];
        if (stepTitles[modalStep]) {
            setModalTitle(stepTitles[modalStep]);
        }
    }, [modalStep]);

    // =========================================================================
    // API CALLS
    // =========================================================================

    const fetchCountries = async () => {
        try {
            const result = await AuthService.getCountries();
            if (result?.success && result?.data) {
                setCountries(result.data);
            }
        } catch (error) {
            console.error("Failed to fetch countries:", error);
        }
    };

    const fetchKycConfig = async (countryCode) => {
        setLoadingConfig(true);
        try {
            const result = await AuthService.getKycConfig(countryCode);
            if (result?.success && result?.data) {
                const config = result.data;
                setKycConfig(config);

                const idCodes = (config.id_documents || []).map(getKycDocumentCode).filter(Boolean);
                const taxCodes = (config.tax_documents || []).map(getKycDocumentCode).filter(Boolean);

                setModalIdType((prev) => {
                    const next = prev && idCodes.includes(prev) ? prev : "";
                    if (!next) {
                        setAadhar("");
                        setDocumentNumberError("");
                    }
                    return next;
                });
                setModalTaxType((prev) => {
                    const next = prev && taxCodes.includes(prev) ? prev : "";
                    if (!next) {
                        setPanCard("");
                        setTaxDocumentError("");
                    }
                    return next;
                });
            } else {
                setKycConfig(null);
                setModalIdType("");
                setModalTaxType("");
            }
        } catch (error) {
            console.error("Failed to fetch KYC config:", error);
            setKycConfig(null);
        } finally {
            setLoadingConfig(false);
        }
    };

    const fetchKycStatus = async () => {
        try {
            const result = await AuthService.getKycStatus();
            if (result?.success && result?.data) {
                const data = result.data;

                // Store individual document statuses
                setIdDocStatus(data.id_document_status || null);
                setTaxDocStatus(data.tax_document_status || null);
                setSelfieStatus(data.selfie_status || null);

                // Store document types user submitted
                if (data.kyc_data) {
                    setSubmittedIdDocType(data.kyc_data.id_document_type || null);
                    setSubmittedTaxDocType(data.kyc_data.tax_document_type || null);
                    setExistingCountryCode(data.kyc_data.country_code || '');

                    // Store existing document numbers for resubmission
                    if (data.kyc_data.id_document_number) {
                        setExistingIdDocNumber(data.kyc_data.id_document_number);
                        setResubmitIdNumber(data.kyc_data.id_document_number);
                    }
                    if (data.kyc_data.tax_document_number) {
                        setExistingTaxDocNumber(data.kyc_data.tax_document_number);
                        setResubmitTaxNumber(data.kyc_data.tax_document_number);
                    }
                }

                if (data.needs_resubmission) {
                    setNeedsResubmission(true);
                    setDocumentsToResubmit(data.documents_needing_resubmission || []);
                } else {
                    setNeedsResubmission(false);
                    setDocumentsToResubmit([]);
                }
            }
        } catch (error) {
            console.error("Failed to fetch KYC status:", error);
        }
    };

    // =========================================================================
    // MODAL NAVIGATION
    // =========================================================================

    // Main KYC modal has 6 steps (0-5)
    const TOTAL_MAIN_MODAL_STEPS = 6;
    
    const nextModalStep = () => {
        setModalStep((prevStep) => {
            if (prevStep < TOTAL_MAIN_MODAL_STEPS - 1) {
                return prevStep + 1;
            }
            return prevStep;
        });
    };

    const prevModalStep = () => {
        setModalStep((prevStep) => {
            if (prevStep > 0) {
                return prevStep - 1;
            }
            return prevStep;
        });
    };

    const resetModalForm = () => {
        setModalStep(0);
        setModalTitle("Select Country and ID Type");
        setModalCountry("");
        setModalIdType("");
        setModalTaxType("");
        setAadhar("");
        setPanCard("");
        setLocalFront("");
        setLocalBack("");
        setLocalPanCard("");
        setLocalSelfie("");
        setPreviewImages({ selfie: "", doc_front: "", doc_back: "", pan: "" });
        stopCamera();
        setIsCameraActive(false);
        setIsCameraReady(false);
        setCameraError("");
        setSelfieCaptured(false);
        setDocumentNumberError("");
        setTaxDocumentError("");
        setKycConfig(null);
    };

    // Open resubmit modal with only rejected documents
    const openResubmitModal = () => {
        setResubmitStep(0); // Use separate state for resubmit modal

        // Reset document numbers to existing values
        setResubmitIdNumber(existingIdDocNumber);
        setResubmitTaxNumber(existingTaxDocNumber);

        // Clear previous uploads
        setLocalFront("");
        setLocalBack("");
        setLocalPanCard("");
        setLocalSelfie("");
        setPreviewImages({ selfie: "", doc_front: "", doc_back: "", pan: "" });

        // Open the modal
        const modalElement = document.getElementById('kycResubmitModal');
        if (modalElement) {
            const modal = new window.bootstrap.Modal(modalElement);
            modal.show();
        }
    };

    // Open KYC modal fresh (reset form and start from step 0)
    const openKycModalFresh = () => {
        resetModalForm();
        setModalStep(0);
        const modalElement = document.getElementById('kycModal');
        if (modalElement) {
            const modal = new window.bootstrap.Modal(modalElement);
            modal.show();
        }
    };

    // =========================================================================
    // VALIDATION
    // =========================================================================

    const MIN_DOCUMENT_NUMBER_LENGTH = 5;

    const normalizeDocNumber = (value) => {
        if (!value || typeof value !== 'string') return '';
        return value.trim().toUpperCase().replace(/\s/g, '');
    };

    const validateDocNumber = (value) => {
        const normalized = normalizeDocNumber(value);
        if (!normalized) return { valid: false, message: 'Document number is required' };
        if (normalized.length < MIN_DOCUMENT_NUMBER_LENGTH) {
            return { valid: false, message: 'Minimum 5 characters required' };
        }
        return { valid: true, normalized };
    };

    const validateModalStep0 = () => {
        let isValid = true;
        const countryError = document.getElementById('countryError');
        const idTypeError = document.getElementById('idTypeError');

        if (!modalCountry) {
            if (countryError) countryError.classList.remove('d-none');
            alertErrorMessage("Country is required");
            isValid = false;
        } else {
            if (countryError) countryError.classList.add('d-none');
        }

        if (!modalIdType) {
            if (idTypeError) idTypeError.classList.remove('d-none');
            isValid = false;
        } else {
            if (idTypeError) idTypeError.classList.add('d-none');
        }

        return isValid;
    };

    const validateAddressFields = () => {
        if (!modalCountry) {
            alertErrorMessage("Country is required");
            return false;
        }
        if (!(address || "").trim()) {
            alertErrorMessage("Address is required");
            return false;
        }
        if (!(city || "").trim()) {
            alertErrorMessage("City is required");
            return false;
        }
        return true;
    };

    const handleDocumentNumberChange = (value) => {
        const normalized = value.toUpperCase().replace(/\s/g, '');
        setAadhar(normalized);

        if (!normalized) {
            setDocumentNumberError("");
            return;
        }

        const validation = validateDocNumber(normalized);
        setDocumentNumberError(validation.valid ? "" : validation.message);
    };

    const handlePanCardChange = (value) => {
        const normalized = value.toUpperCase().replace(/\s/g, '');
        setPanCard(normalized);

        if (!normalized) {
            setTaxDocumentError("");
            return;
        }

        const validation = validateDocNumber(normalized);
        setTaxDocumentError(validation.valid ? "" : validation.message);
    };

    // =========================================================================
    // FILE HANDLERS
    // =========================================================================

    const validateFileUpload = (file) => {
        if (!file) return { valid: false, message: 'No file selected' };
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            return { valid: false, message: 'Only PNG, JPEG, and JPG files are allowed' };
        }
        if (file.size > 5 * 1024 * 1024) {
            return { valid: false, message: 'File size must be less than 5MB' };
        }
        return { valid: true };
    };

    const handleChangeIdentity = (event) => {
        const file = event.target.files[0];
        const validation = validateFileUpload(file);
        if (!validation.valid) {
            alertErrorMessage(validation.message);
            return;
        }
        setLocalFront(file);
        setPreviewImages(prev => ({ ...prev, doc_front: URL.createObjectURL(file) }));
    };

    const handleChangeIdentity2 = (event) => {
        const file = event.target.files[0];
        const validation = validateFileUpload(file);
        if (!validation.valid) {
            alertErrorMessage(validation.message);
            return;
        }
        setLocalBack(file);
        setPreviewImages(prev => ({ ...prev, doc_back: URL.createObjectURL(file) }));
    };

    const handleChangePanCard = (event) => {
        const file = event.target.files[0];
        const validation = validateFileUpload(file);
        if (!validation.valid) {
            alertErrorMessage(validation.message);
            return;
        }
        setLocalPanCard(file);
        setPreviewImages(prev => ({ ...prev, pan: URL.createObjectURL(file) }));
    };

    // Generic file change handler for resubmit modal
    const handleFileChange = (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const validation = validateFileUpload(file);
        if (!validation.valid) {
            alertErrorMessage(validation.message);
            return;
        }

        if (type === 'id_front') {
            setLocalFront(file);
            setPreviewImages(prev => ({ ...prev, doc_front: URL.createObjectURL(file) }));
        } else if (type === 'id_back') {
            setLocalBack(file);
            setPreviewImages(prev => ({ ...prev, doc_back: URL.createObjectURL(file) }));
        } else if (type === 'pan') {
            setLocalPanCard(file);
            setPreviewImages(prev => ({ ...prev, pan: URL.createObjectURL(file) }));
        } else if (type === 'selfie') {
            stopCamera();
            setIsCameraActive(false);
            setLocalSelfie(file);
            setPreviewImages(prev => ({ ...prev, selfie: URL.createObjectURL(file) }));
            setSelfieCaptured(true);
        }
    };

    // =========================================================================
    // CAMERA / SELFIE
    // =========================================================================

    const startCamera = async () => {
        try {
            setCameraError("");
            setIsCameraReady(false);

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setCameraError("Camera not supported on this device");
                return;
            }

            // Set camera active first so the video element renders
            setIsCameraActive(true);

            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }
            });

            streamRef.current = stream;

            // Wait a bit for the video element to be rendered
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.onloadedmetadata = () => {
                        if (videoRef.current) {
                            videoRef.current.play().then(() => {
                                setIsCameraReady(true);
                            }).catch((err) => {
                                console.error("Video play error:", err);
                                setIsCameraReady(true); // Still set ready even if autoplay fails
                            });
                        }
                    };
                    // Fallback - if onloadedmetadata doesn't fire, check after a delay
                    setTimeout(() => {
                        if (videoRef.current && videoRef.current.srcObject && !isCameraReady) {
                            setIsCameraReady(true);
                        }
                    }, 1000);
                }
            }, 100);

        } catch (error) {
            console.error("Camera error:", error);
            setIsCameraActive(false);
            if (error.name === "NotAllowedError") {
                setCameraError("Camera permission denied. Please allow camera access.");
            } else if (error.name === "NotFoundError") {
                setCameraError("No camera found on this device.");
            } else {
                setCameraError("Failed to access camera. Please try again.");
            }
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setIsCameraActive(false);
        setIsCameraReady(false);
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
                setLocalSelfie(file);
                setPreviewImages(prev => ({ ...prev, selfie: URL.createObjectURL(blob) }));
                setSelfieCaptured(true);
                stopCamera();
            }
        }, "image/jpeg", 0.9);
    };

    const retakeSelfie = () => {
        setLocalSelfie("");
        setPreviewImages(prev => ({ ...prev, selfie: "" }));
        setSelfieCaptured(false);
        startCamera();
    };

    // =========================================================================
    // KYC SUBMIT
    // =========================================================================

    const handleModalKycSubmit = async () => {
        if (!validateAddressFields()) {
            return;
        }

        const idDocumentType = modalIdType?.trim();
        if (!idDocumentType) {
            alertErrorMessage("Please select an ID document type");
            return;
        }

        const formData = new FormData();

        // Get country name from countries list
        const selectedCountry = countries.find(c => c.code === modalCountry);
        const countryName = selectedCountry?.name || modalCountry;

        // Personal info
        formData.append("first_name", firstName.trim());
        formData.append("last_name", lastName.trim());
        formData.append("gender", gender);
        formData.append("date_of_birth", infoDob);
        formData.append("nationality", countryName);

        // Address
        formData.append("address_line1", address.trim());
        formData.append("city", city.trim());
        formData.append("state", infoState.trim());
        formData.append("postal_code", zipCode.trim().toUpperCase());
        formData.append("country_code", modalCountry);
        formData.append("country_name", countryName);

        // ID Document
        formData.append("id_document_type", idDocumentType);
        formData.append("id_document_number", normalizeDocNumber(aadhar));
        formData.append("id_front_image", localFront);
        if (localBack) formData.append("id_back_image", localBack);

        // Tax Document (optional — only send when provided)
        const taxNumberNorm = normalizeDocNumber(panCard);
        const taxDocumentType = modalTaxType?.trim();
        if (taxDocumentType) formData.append("tax_document_type", taxDocumentType);
        if (taxNumberNorm) formData.append("tax_document_number", taxNumberNorm);
        if (localPanCard) formData.append("tax_document_image", localPanCard);

        // Selfie
        formData.append("selfie_image", localSelfie);
        formData.append("selfie_capture_method", "camera");
        formData.append("selfie_device_info", navigator.userAgent);

        // Resubmission - only for PARTIAL rejection (kycVerfied === 4) where specific documents need resubmission
        // For FULL rejection (kycVerfied === 3), submit as fresh KYC (no is_resubmission flag)
        // The backend will replace the entire KYC record for fresh submissions
        const isPartialRejection = kycVerfied === 4 || kycVerfied === "4";
        const validDocsToResubmit = documentsToResubmit.filter(d => d && d.type);
        
        if (isPartialRejection && needsResubmission && validDocsToResubmit.length > 0) {
            formData.append("is_resubmission", "true");
            // Send specific resubmitting flags that backend expects
            validDocsToResubmit.forEach(doc => {
                if (doc.type === 'id_document') {
                    formData.append("resubmitting_id_document", "true");
                } else if (doc.type === 'tax_document') {
                    formData.append("resubmitting_tax_document", "true");
                } else if (doc.type === 'selfie') {
                    formData.append("resubmitting_selfie", "true");
                }
            });
        }

        try {
            LoaderHelper.loaderStatus(true);
            const result = await AuthService.addkyc(formData);
            if (result?.success) {
                alertSuccessMessage(result?.message || "KYC submitted successfully");

                const kycModalElement = document.getElementById('kycModal');
                if (kycModalElement) {
                    const kycModal = window.bootstrap?.Modal?.getInstance(kycModalElement);
                    if (kycModal) kycModal.hide();
                }

                // Remove any modal backdrops
                document.body.classList.remove('modal-open');
                document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());

                // Show success modal
                const submitModalElement = document.getElementById('kycSubmitModal');
                if (submitModalElement) {
                    setTimeout(() => {
                        const submitModal = new window.bootstrap.Modal(submitModalElement);
                        submitModal.show();
                    }, 400);
                }

                resetModalForm();
                handleUserDetails();
            } else {
                // On error, just show error message - don't close modals
                alertErrorMessage(result?.message || "Failed to submit KYC. Please try again.");
            }
        } catch (error) {
            console.error("Error in handleModalKycSubmit:", error);
            // On error, just show error message - don't close modals
            alertErrorMessage(error?.response?.data?.message || error?.message || "An error occurred. Please try again.");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    // =========================================================================
    // RESUBMIT KYC (partial - only rejected documents)
    // =========================================================================

    const handleResubmitKyc = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const formData = new FormData();

            // Always mark as resubmission
            formData.append("is_resubmission", "true");

            // Add country code and document types for validation
            if (existingCountryCode) {
                formData.append("country_code", existingCountryCode);
            }

            // Only add documents that need resubmission
            if (needsIdDocResubmit()) {
                formData.append("resubmitting_id_document", "true");
                formData.append("id_front_image", localFront);
                if (localBack) formData.append("id_back_image", localBack);

                // Include document number (even if unchanged, for validation)
                if (resubmitIdNumber) {
                    formData.append("id_document_number", resubmitIdNumber.trim());
                    formData.append("id_document_type", submittedIdDocType);
                }
            }

            if (needsTaxDocResubmit()) {
                formData.append("resubmitting_tax_document", "true");
                formData.append("tax_document_image", localPanCard);

                // Include document number (even if unchanged, for validation)
                if (resubmitTaxNumber) {
                    formData.append("tax_document_number", resubmitTaxNumber.trim());
                    formData.append("tax_document_type", submittedTaxDocType);
                }
            }

            if (needsSelfieResubmit()) {
                formData.append("resubmitting_selfie", "true");
                formData.append("selfie_image", localSelfie);
                formData.append("selfie_capture_method", "camera");
                formData.append("selfie_device_info", navigator.userAgent);
            }

            LoaderHelper.loaderStatus(true);
            const result = await AuthService.addkyc(formData);

            if (result?.success) {
                alertSuccessMessage(result?.message || "Documents resubmitted successfully");

                // Close resubmit modal
                const resubmitModalElement = document.getElementById('kycResubmitModal');
                if (resubmitModalElement) {
                    const resubmitModal = window.bootstrap?.Modal?.getInstance(resubmitModalElement);
                    if (resubmitModal) resubmitModal.hide();
                }

                // Clean up
                document.body.classList.remove('modal-open');
                document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());

                // Show success modal
                const submitModalElement = document.getElementById('kycSubmitModal');
                if (submitModalElement) {
                    setTimeout(() => {
                        const submitModal = new window.bootstrap.Modal(submitModalElement);
                        submitModal.show();
                    }, 400);
                }

                resetModalForm();
                setNeedsResubmission(false);
                setDocumentsToResubmit([]);
                handleUserDetails();
                fetchKycStatus();
            } else {
                alertErrorMessage(result?.message || "Failed to resubmit documents. Please try again.");
            }
        } catch (error) {
            console.error("Error in handleResubmitKyc:", error);
            alertErrorMessage(error?.response?.data?.message || error?.message || "An error occurred. Please try again.");
        } finally {
            LoaderHelper.loaderStatus(false);
            setIsSubmitting(false);
        }
    };

    // =========================================================================
    // HELPERS
    // =========================================================================

    const getIdDocConfig = () => {
        if (!kycConfig || !modalIdType) return null;
        return kycConfig.id_documents.find((d) => getKycDocumentCode(d) === modalIdType);
    };

    const getTaxDocConfig = () => {
        if (!kycConfig || !modalTaxType) return null;
        return kycConfig.tax_documents.find((d) => getKycDocumentCode(d) === modalTaxType);
    };

    /** India requires PAN/tax document; other countries treat tax as optional on this step. */
    const isTaxDocumentRequiredForCountry = () => modalCountry === 'IN';

    /** Validates type, number, file, and format when tax section must be complete. */
    const validateFilledTaxDocumentStep = () => {
        const taxNumberNorm = normalizeDocNumber(panCard);
        if (!modalTaxType) {
            alertErrorMessage('Please select a tax document type');
            return false;
        }
        if (!taxNumberNorm) {
            alertErrorMessage('Please enter Tax Identification Number');
            return false;
        }
        if (!localPanCard) {
            alertErrorMessage('Please upload Tax document');
            return false;
        }
        const validation = validateDocNumber(panCard);
        if (!validation.valid) {
            alertErrorMessage(validation.message || 'Please enter a valid Tax ID');
            setTaxDocumentError(validation.message);
            return false;
        }
        return true;
    };

    // Check if a specific document needs resubmission
    const needsIdDocResubmit = () => {
        return documentsToResubmit.some(d => d.type === 'id_document');
    };

    const needsTaxDocResubmit = () => {
        return documentsToResubmit.some(d => d.type === 'tax_document');
    };

    const needsSelfieResubmit = () => {
        return documentsToResubmit.some(d => d.type === 'selfie');
    };

    // Get rejection reason for a specific document
    const getRejectReason = (docType) => {
        const doc = documentsToResubmit.find(d => d.type === docType);
        return doc?.reason || '';
    };

    // Get status display for a document
    const getDocStatusDisplay = (status) => {
        console.log("🚀 ~ getDocStatusDisplay ~ status:", status)
        switch (status) {
            case 'approved':
                return { icon: '✅', text: 'Approved', className: 'text-success' };
            case 'rejected':
                return { icon: '❌', text: 'Rejected', className: 'text-white' };
            case 'resubmit_required':
                return { icon: '⚠️', text: 'Resubmission Required', className: 'text-white' };
            case 'pending':
            default:
                return { icon: '⏳', text: 'Under Review', className: 'text-white' };
        }
    };

    // Get human-readable document type name
    const getDocTypeName = (docTypeCode) => {
        const docTypeNames = {
            // India
            'AADHAAR': 'Aadhaar Card',
            'PAN': 'PAN Card',
            'TAX_ID': 'TAX ID',
            // Common international
            'PASSPORT': 'Passport',
            'NATIONAL_ID': 'National ID Card',
            'DRIVING_LICENSE': 'Driving License',
            'RESIDENCE_PERMIT': 'Residence Permit',
            // Tax documents
            'SSN': 'Social Security Number',
            'TIN': 'Tax Identification Number',
            'NIN': 'National Insurance Number',
            'TFN': 'Tax File Number',
            'STEUER_ID': 'Steueridentifikationsnummer',
            'NRIC': 'NRIC',
            'EMIRATES_ID': 'Emirates ID'
        };
        return docTypeNames[docTypeCode] || docTypeCode || 'ID Document';
    };

    const faqData = [
        { q: "How long does KYC take?", a: "KYC verification usually takes 24-48 hours after submission." },
        { q: "What documents do I need for KYC?", a: "A valid government-issued ID and tax document are required." },
        { q: "Can I use the app without completing KYC?", a: "Limited features are available, but full access requires KYC." },
        { q: "Is my personal information secure in the KYC process?", a: "Your data is encrypted and handled according to strict security standards." },

        { q: "Can I resubmit my KYC if it gets rejected?", a: "Yes, if your KYC is rejected or partially rejected, you can reupload the requested documents and resubmit." },
        { q: "Do I need to upload both front and back of my ID?", a: "Some ID documents require both front and back images. The upload fields will appear based on the selected document type." },
        { q: "Is live selfie mandatory for KYC?", a: "Yes, a live selfie captured through your device camera is required to complete KYC verification." }
    ];


    useEffect(() => {
       window.scrollTo(0, 0);
    }, []);


    // =========================================================================
    // RENDER
    // =========================================================================

    return (
        <>
            <div className="dashboard_right">

                <div className="kyc_verif_bnr_wrapper">

                    <div className="profile_sections" >
                        <div className="row" >
                            <div className="col-md-12" >
                                <h2 className="mb-0 pb-0"> KYC Verification </h2>
                            </div>
                        </div>
                    </div>

                    {/* Not Verified - Show verify button */}
                    {(kycVerfied === "" || kycVerfied === 0 || kycVerfied === "0") && (
                        <div className="kyc_verif_bnr">
                            <div className="kysbnr_cnt">
                                <h5>KYC</h5>
                                <p>Finish your KYC in just a few minutes and enjoy a seamless experience. Submit your basic details once and get instant access to
                                    withdrawals, rewards, and every feature without any delays or limitations.</p>

                                <h6>KYC Verification Requirements</h6>

                                <ul className="kyclist">
                                    <li><img src="/images/staricon.png" alt="star" /> ID Document</li>
                                    <li><img src="/images/staricon.png" alt="star" /> Tax Document</li>
                                    <li><img src="/images/staricon.png" alt="star" /> Live Selfie (Camera Required)</li>
                                </ul>

                                <button className="kyc btn" onClick={openKycModalFresh}>Verify </button>
                            </div>
                            <div className="kycvector">
                                <img src="/images/kyc_verification_vector.svg" alt="kyc" />
                            </div>
                        </div>
                    )}

                    {/* Pending Verification */}
                    {(kycVerfied === 1 || kycVerfied === "1") && (
                        <div className="kyc_verif_bnr kyc_pending">
                            <div className="kysbnr_cnt">
                                <h5>KYC Pending</h5>
                                <p>Your KYC application has been submitted and is currently under review. You will be notified once the verification is complete.</p>

                                <h6>Documents Submitted</h6>

                                <ul className="kyclist">
                                    <li className={getDocStatusDisplay(idDocStatus).className}>
                                        {getDocStatusDisplay(idDocStatus).icon} {submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'Identity Document'} - {getDocStatusDisplay(idDocStatus).text}
                                    </li>
                                    <li className={getDocStatusDisplay(taxDocStatus).className}>
                                        {getDocStatusDisplay(taxDocStatus).icon} {submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'} - {getDocStatusDisplay(taxDocStatus).text}
                                    </li>
                                    <li className={getDocStatusDisplay(selfieStatus).className}>
                                        {getDocStatusDisplay(selfieStatus).icon} Live Selfie - {getDocStatusDisplay(selfieStatus).text}
                                    </li>
                                </ul>
                            </div>
                            <div className="kycvector">
                                <img src="/images/kyc_verification_vector.svg" alt="kyc" />
                            </div>
                        </div>
                    )}

                    {/* Verified/Approved */}
                    {(kycVerfied === 2 || kycVerfied === "2") && (
                        <div className="kyc_verif_bnr kyc_seccessfull">
                            <div className="kysbnr_cnt">
                                <h5>KYC Verified <i class="ri-verified-badge-line text-success"></i></h5>
                                <p>Congratulations! Your KYC verification has been approved. You now have full access to all platform features.</p>

                                <h6>Your Benefits</h6>

                                <ul className="kyclist">
                                    <li>✅ Deposit & Withdraw Without Limit</li>
                                    <li>✅ Spot & Futures Trading Unlock</li>
                                    <li>✅ 100% Secure Trading with Verified KYC</li>
                                </ul>
                            </div>
                            <div className="kycvector">
                                <img src="/images/kyc_success_vector.svg" alt="kyc" />
                            </div>
                        </div>
                    )}

                    {/* Complete Rejection */}
                    {(kycVerfied === 3 || kycVerfied === "3") && (
                        <div className="kyc_verif_bnr kyc_rejected">
                            <div className="kysbnr_cnt">
                                <h5>KYC Rejected</h5>
                                <p>Unfortunately, your KYC application has been rejected. Please review the reason below and resubmit with correct documents.</p>

                                <h6>Rejection Reason</h6>

                                <ul className="kyclist">
                                    <li>❌ {reason || 'KYC requirements not met'}</li>
                                </ul>

                                <button className="kyc btn" onClick={openKycModalFresh}>Resubmit KYC </button>
                            </div>
                            <div className="kycvector">
                                <img src="/images/rejectvector.png" alt="kyc" />
                            </div>
                        </div>
                    )}

                    {/* Partial Rejection - Some documents need resubmission */}
                    {(kycVerfied === 4 || kycVerfied === "4") && (
                        <div className="kyc_verif_bnr kyc_rejected">
                            <div className="kysbnr_cnt">
                                <h5>Documents Need Resubmission</h5>
                                <p>Some of your documents require resubmission. Please check the details below and upload the corrected documents.</p>

                                <h6>Document Status</h6>

                                <ul className="kyclist">
                                    <li className={getDocStatusDisplay(idDocStatus).className}>
                                        {getDocStatusDisplay(idDocStatus).icon} {submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'Identity Document'} - {getDocStatusDisplay(idDocStatus).text}
                                        {idDocStatus === 'resubmit_required' && getRejectReason('id_document') && (
                                            <small className="d-block text-danger ms-2">Reason: {getRejectReason('id_document')}</small>
                                        )}
                                    </li>
                                    <li className={getDocStatusDisplay(taxDocStatus).className}>
                                        {getDocStatusDisplay(taxDocStatus).icon} {submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'} - {getDocStatusDisplay(taxDocStatus).text}
                                        {taxDocStatus === 'resubmit_required' && getRejectReason('tax_document') && (
                                            <small className="d-block text-danger ms-2">Reason: {getRejectReason('tax_document')}</small>
                                        )}
                                    </li>
                                    <li className={getDocStatusDisplay(selfieStatus).className}>
                                        {getDocStatusDisplay(selfieStatus).icon} Live Selfie - {getDocStatusDisplay(selfieStatus).text}
                                        {selfieStatus === 'rejected' && getRejectReason('selfie') && (
                                            <small className="d-block text-danger ms-2">Reason: {getRejectReason('selfie')}</small>
                                        )}
                                    </li>
                                </ul>

                                <button className="kyc btn" onClick={openResubmitModal}>Resubmit Documents</button>
                            </div>
                            <div className="kycvector">
                                <img src="/images/rejectvector.png" alt="kyc" />
                            </div>
                        </div>
                    )}


                    <div className="kyc_account d-flex">
                        <div className="account_benifits">
                            <h5>Account Benefits</h5>

                            <div className="row">
                                <div className="col-sm-4">
                                    <h6>Level</h6>
                                    <ul className="kyclist">
                                        <li><img src="/images/staricon.png" alt="star" /> KYC Level</li>
                                        <li><img src="/images/staricon.png" alt="star" /> Crypto Deposit</li>
                                        <li><img src="/images/staricon.png" alt="star" /> Crypto Withdrawal</li>
                                        <li><img src="/images/staricon.png" alt="star" /> Crypto Swap</li>
                                        <li><img src="/images/staricon.png" alt="star" /> Spot/Futures Trading</li>
                                        <li><img src="/images/staricon.png" alt="star" /> Platform Events</li>
                                    </ul>
                                </div>

                                <div className="col-sm-4">
                                    <h6>Unverified</h6>
                                    <ul className="kyclist">
                                        <li>Unlimited</li>
                                        <li>1 BTC per day</li>
                                        <li><img src="/images/closebtn2.svg" alt="star" /></li>
                                        <li><img src="/images/closebtn2.svg" alt="star" /></li>
                                        <li><img src="/images/closebtn2.svg" alt="star" /></li>
                                        <li><img src="/images/rightbtn2.svg" alt="star" /></li>
                                    </ul>
                                </div>

                                <div className="col-sm-4">
                                    <h6>Advanced KYC</h6>
                                    <ul className="kyclist">
                                        <li>Unlimited</li>
                                        <li>100 BTC per day*</li>
                                        <li>30,000 USD per day*</li>
                                        <li><img src="/images/rightbtn2.svg" alt="star" /></li>
                                        <li><img src="/images/rightbtn2.svg" alt="star" /></li>
                                        <li><img src="/images/rightbtn2.svg" alt="star" /></li>
                                    </ul>
                                </div>

                            </div>
                        </div>


                        <div className="faq_section">
                            <h4>Faq</h4>
                            <div className="table-responsive">
                                {faqData.map((item, index) => (
                                    <div
                                        className={`faq_item ${activeIndex === index ? "active" : ""}`}
                                        key={index}
                                    >
                                        <button
                                            className="faq_question"
                                            onClick={() =>
                                                setActiveIndex(activeIndex === index ? null : index)
                                            }
                                        >
                                            {item.q}
                                            <span className="icon"><i className="ri-arrow-down-s-line"></i></span>
                                        </button>

                                        <div className="faq_answer">
                                            <p>{item.a}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>

                </div>


                {/* <!-- Modal kyc Start --> */}

                <div className="modal fade kyc_modal" id="kycModal" tabIndex="-1" data-bs-backdrop="static">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">


                            <div className="modal-header">
                                <h5 className="modal-title" id="kycTitle">{modalTitle}</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                            </div>


                            <div className="modal-body">


                                {/* Step 0: Select Country and ID Type */}
                                {modalStep === 0 && (
                                <div className="kyc_step active" data-title="Select Country and ID Type">
                                    <label className="label">🌟 Country/Region (Please select the issuing country of the document) <span className="text-danger">*</span></label>
                                    <div className="select_box">
                                        <select
                                            value={modalCountry}
                                            onChange={(e) => setModalCountry(e.target.value)}
                                            id="kycCountry"
                                        >
                                            <option value="">Select Country</option>
                                            {countries.map((c) => (
                                                <option key={c.code} value={c.code}>
                                                    {c.flag} {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <small className="text-danger d-none" id="countryError">Please select a country</small>

                                    {loadingConfig && <p className="text-muted mt-2">Loading document options...</p>}

                                    {kycConfig && !loadingConfig && (
                                        <>
                                            <label className="label mt-4">ID Type <span className="text-danger">*</span></label>
                                            <div className="id_grid">
                                                {kycConfig.id_documents.map((doc) => {
                                                    const docCode = getKycDocumentCode(doc);
                                                    if (!docCode) return null;
                                                    return (
                                                    <label key={docCode} className={`id_item ${modalIdType === docCode ? 'selected' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="kycIdType"
                                                            value={docCode}
                                                            checked={modalIdType === docCode}
                                                            onChange={(e) => setModalIdType(e.target.value)}
                                                        />
                                                        {doc.label}
                                                    </label>
                                                    );
                                                })}
                                            </div>
                                            <small className="text-danger d-none" id="idTypeError">Please select an ID type</small>
                                        </>
                                    )}

                                    <button type="button" className="primary_btn nextStep" onClick={(e) => {
                                        e.preventDefault();
                                        if (validateModalStep0()) {
                                            nextModalStep();
                                        }
                                    }}>Next</button>
                                </div>
                                )}

                                {/* Step 1: Personal Details */}
                                {modalStep === 1 && (
                                <div className="kyc_step active" data-title="Personal Details">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="label">First Name <span className="text-danger">*</span></label>
                                            <input
                                                className="input"
                                                placeholder="Enter First Name"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="label">Last Name <span className="text-danger">*</span></label>
                                            <input
                                                className="input"
                                                placeholder="Enter Last Name"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="label">Date of Birth <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                className="input"
                                                value={infoDob}
                                                onChange={(e) => setInfoDob(e.target.value)}
                                                max={moment().subtract(18, 'years').format('YYYY-MM-DD')}
                                            />
                                            {/* <small className="text-muted">You must be at least 18 years old</small> */}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="label">Gender <span className="text-danger">*</span></label>
                                            <select className="input" value={gender} onChange={(e) => setGender(e.target.value)}>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="label">Address <span className="text-danger">*</span></label>
                                            <input
                                                id="kycAddress"
                                                className="input"
                                                placeholder="Enter your full address (Minimum 10 characters)"
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                            />
                                            {/* <small className="text-muted">Minimum 10 characters</small> */}
                                        </div>
                                        <div className="col-md-4">
                                            <label className="label">City <span className="text-danger">*</span></label>
                                            <input
                                                id="kycCity"
                                                className="input"
                                                placeholder="City"
                                                value={city}
                                                onChange={(e) => setCity(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="label">State <span className="text-danger">*</span></label>
                                            <input
                                                className="input"
                                                placeholder="State"
                                                value={infoState}
                                                onChange={(e) => setInfoState(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="label">Zip/Postal Code <span className="text-danger">*</span></label>
                                            <input
                                                className="input"
                                                placeholder="Zip Code"
                                                value={zipCode}
                                                onChange={(e) => setZipCode(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                                        <button className="primary_btn prevStep" onClick={(e) => { e.preventDefault(); prevModalStep(); }}>Back</button>
                                        <button type="button" className="primary_btn nextStep" onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            
                                            // Validate all fields before proceeding
                                            let hasError = false;
                                            
                                            if (!firstName || firstName.trim().length < 2) { 
                                                alertErrorMessage('Please enter a valid first name (at least 2 characters)'); 
                                                hasError = true;
                                            } else if (!lastName || lastName.trim().length < 1) { 
                                                alertErrorMessage('Please enter a valid last name'); 
                                                hasError = true;
                                            } else if (!infoDob) { 
                                                alertErrorMessage('Please enter your date of birth'); 
                                                hasError = true;
                                            } else {
                                                const age = moment().diff(moment(infoDob), 'years');
                                                if (age < 18) { 
                                                    alertErrorMessage('You must be at least 18 years old'); 
                                                    hasError = true;
                                                }
                                            }
                                            
                                            if (!hasError && !validateAddressFields()) {
                                                hasError = true;
                                            }
                                            if (!hasError && (!infoState || infoState.trim().length < 2)) { 
                                                alertErrorMessage('Please enter a valid state'); 
                                                hasError = true;
                                            }
                                            if (!hasError && (!zipCode || zipCode.trim().length < 3)) { 
                                                alertErrorMessage('Please enter a valid zip/postal code'); 
                                                hasError = true;
                                            }
                                            
                                            // Only proceed if no validation errors
                                            if (!hasError) {
                                                nextModalStep();
                                            }
                                        }}>Next</button>
                                    </div>
                                </div>
                                )}

                                {/* Step 2: Take a Photo of Your ID Card */}
                                {modalStep === 2 && (
                                <div className="kyc_step active" data-title="Take a Photo of Your ID Card">
                                    <div className="id_preview">
                                        <img src="/images/photoid_vector.png" alt="ID card" />
                                    </div>

                                    <div className="tips photomini">
                                        <p><img src="/images/photoidmini.png" alt="tip 1" /></p>
                                        <p><img src="/images/photoidmini2.png" alt="tip 2" /></p>
                                        <p><img src="/images/photoidmini3.png" alt="tip 3" /></p>
                                    </div>

                                    <h6 className="text-dark">The selected country/region and ID type are as follows:</h6>

                                    <div className="info_text">
                                        <ul className="d-flex gap-3 text-dark">
                                            <li>
                                                {countries.find(c => c.code === modalCountry)?.flag || '🌍'}
                                                {' '}{countries.find(c => c.code === modalCountry)?.name || 'Not Selected'}
                                            </li>
                                            <li><img src="/images/idcard.png" alt="ID Card" /> {getIdDocConfig()?.label || 'Not Selected'}</li>
                                        </ul>
                                    </div>
                                    <p>Please upload a valid ID matching your selected country/region and ID type to avoid verification failure.</p>

                                    <input
                                        className={`input ${documentNumberError ? 'is-invalid' : ''}`}
                                        placeholder={getIdDocConfig()?.label ? `${getIdDocConfig().label} Number` : "ID Card Number"}
                                        value={aadhar}
                                        onChange={(e) => handleDocumentNumberChange(e.target.value)}
                                    />
                                    {documentNumberError && <small className="text-danger">{documentNumberError}</small>}
                                    {/* {getIdDocConfig() && <small className="text-muted d-block">{getIdDocConfig().min}-{getIdDocConfig().max} characters</small>} */}

                                    <div className="upload_grid">

                                        <div className="upload-box">
                                            <input type="file" id="modalDocFront" hidden accept="image/png,image/jpeg,image/jpg" onChange={handleChangeIdentity} />
                                            <label htmlFor="modalDocFront" className="upload-label">
                                                {previewImages?.doc_front ? (
                                                    <img src={previewImages.doc_front} alt="Document Front" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                                                ) : (
                                                    <img className="upload_back_img" src="/images/fileback_vector.png" alt="upload background" />
                                                )}
                                                <div className="icon">
                                                    <img src="/images/uploadvector.svg" alt="upload" />
                                                </div>
                                                <h3>{localFront ? '✓ Uploaded' : 'Front Side'}</h3>
                                                <p>{localFront?.name || 'Drag or choose file'}</p>
                                            </label>
                                        </div>

                                        <div className="upload-box">
                                            <input type="file" id="modalDocBack" hidden accept="image/png,image/jpeg,image/jpg" onChange={handleChangeIdentity2} />
                                            <label htmlFor="modalDocBack" className="upload-label">
                                                {previewImages?.doc_back ? (
                                                    <img src={previewImages.doc_back} alt="Document Back" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                                                ) : (
                                                    <img className="upload_back_img" src="/images/fileback_vector.png" alt="upload background" />
                                                )}
                                                <div className="icon">
                                                    <img src="/images/uploadvector.svg" alt="upload" />
                                                </div>
                                                <h3>{localBack ? '✓ Uploaded' : 'Back Side'}{getIdDocConfig()?.requires_back_image ? '' : '(Optional)'}</h3>
                                                <p>{localBack?.name || 'Drag or choose file'}</p>
                                            </label>
                                        </div>

                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                                        <button className="primary_btn prevStep" onClick={(e) => { e.preventDefault(); prevModalStep(); }}>Back</button>
                                        <button className="primary_btn nextStep" onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            
                                            let hasError = false;
                                            const docConfig = getIdDocConfig();
                                            
                                            // Validate document number
                                            const idValidation = validateDocNumber(aadhar);
                                            if (!idValidation.valid) {
                                                alertErrorMessage(idValidation.message || 'Please enter a valid document number');
                                                setDocumentNumberError(idValidation.message);
                                                hasError = true;
                                            }
                                            
                                            // Validate front image
                                            if (!hasError && !localFront) { 
                                                alertErrorMessage('Please upload front image of your ID card'); 
                                                hasError = true;
                                            }
                                            
                                            // Validate back image if required
                                            if (!hasError && docConfig?.requires_back_image && !localBack) { 
                                                alertErrorMessage('Please upload back image of your ID card'); 
                                                hasError = true;
                                            }
                                            
                                            // Only proceed if no validation errors
                                            if (!hasError) {
                                                nextModalStep();
                                            }
                                        }}>Next</button>
                                    </div>
                                </div>
                                )}

                                {/* Step 3: Income Tax & Selfie */}
                                {modalStep === 3 && (
                                <div className="kyc_step active" data-title="Income Tax & Selfie">
                                    <div className="d-flex gap-4 flex-column">
                                        <div>
                                            <label className="label mb-2">
                                                Tax Document Type{' '}
                                                <span className="text-muted">(Optional)</span>
                                            </label>
                                            {kycConfig && (
                                                <div className="id_grid mb-3">
                                                    {kycConfig.tax_documents.map((doc) => {
                                                        const docCode = getKycDocumentCode(doc);
                                                        if (!docCode) return null;
                                                        return (
                                                        <label key={docCode} className={`id_item ${modalTaxType === docCode ? 'selected' : ''}`}>
                                                            <input
                                                                type="radio"
                                                                name="kycTaxType"
                                                                value={docCode}
                                                                checked={modalTaxType === docCode}
                                                                onChange={(e) => setModalTaxType(e.target.value)}
                                                            />
                                                            {doc.label}
                                                        </label>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {modalTaxType && (
                                                <>
                                                    <label className="label mb-2">
                                                        {getTaxDocConfig()?.label || 'Tax ID'} Number{' '}
                                                        {isTaxDocumentRequiredForCountry() ? (
                                                            <span className="text-danger">*</span>
                                                        ) : (
                                                            <span className="text-muted">(Optional)</span>
                                                        )}
                                                    </label>
                                                    <input
                                                        className={`input ${taxDocumentError ? 'is-invalid' : ''}`}
                                                        placeholder={`Enter ${getTaxDocConfig()?.label || 'Tax ID'} Number`}
                                                        value={panCard}
                                                        onChange={(e) => handlePanCardChange(e.target.value)}
                                                    />
                                                    {taxDocumentError && <small className="text-danger">{taxDocumentError}</small>}
                                                    {/* {getTaxDocConfig() && <small className="text-muted d-block">{getTaxDocConfig().min}-{getTaxDocConfig().max} characters</small>} */}
                                                </>
                                            )}
                                        </div>

                                        <div>
                                            <h5 className="mb-2 text-dark">
                                                Upload Tax Document{' '}
                                                {isTaxDocumentRequiredForCountry() ? (
                                                    <span className="text-danger">*</span>
                                                ) : (
                                                    <span className="text-muted">(Optional)</span>
                                                )}
                                            </h5>
                                            <span className="small text-muted">(Only JPEG, PNG & JPG formats and file size upto 5MB are supported)</span>
                                            <div className="upload-box mt-2">
                                                <input type="file" id="modalPanCard" hidden accept="image/png,image/jpeg,image/jpg" onChange={handleChangePanCard} />
                                                <label htmlFor="modalPanCard" className="upload-label">
                                                    {previewImages?.pan ? (
                                                        <img src={previewImages.pan} alt="Tax Document" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div className="icon">
                                                            <img src="/images/uploadvector.svg" alt="upload" />
                                                        </div>
                                                    )}
                                                    <h3>{localPanCard ? '✓ Uploaded' : 'Choose a File'}</h3>
                                                    <p>{localPanCard?.name || 'Drag or choose your file to upload'}</p>
                                                </label>
                                            </div>
                                        </div>

                                        <div>
                                            <h5 className="mb-2 text-dark">Selfie <span className="text-danger">*</span></h5>
                                            <span className="small text-muted">(Use your camera to capture live, or upload a photo from your device)</span>

                                            <input
                                                type="file"
                                                ref={selfieInputRef}
                                                hidden
                                                accept="image/png,image/jpeg,image/jpg"
                                                onChange={(e) => handleFileChange(e, 'selfie')}
                                            />

                                            <div className="upload-box mt-2" style={{ minHeight: '200px' }}>
                                                {cameraError && (
                                                    <div className="alert alert-danger text-center p-2 mb-2">
                                                        {cameraError}
                                                    </div>
                                                )}

                                                {!isCameraActive && !selfieCaptured && (
                                                    <div className="text-center p-3">
                                                        <div className="selfie_circle mb-3" style={{ margin: '0 auto' }}>
                                                            <img src="/images/selefvector.png" alt="selfie guide" />
                                                        </div>
                                                        <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
                                                            <button
                                                                type="button"
                                                                className="primary_btn prevStep"
                                                                onClick={startCamera}
                                                            >
                                                                <i className="ri-camera-line me-2"></i> Start Camera
                                                            </button>
                                                            <span className="text-muted">or</span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={() => selfieInputRef.current?.click()}
                                                            >
                                                                <i className="ri-upload-2-line me-2"></i> Upload Photo
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {isCameraActive && !selfieCaptured && (
                                                    <div className="text-center">
                                                        <div style={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            maxWidth: '300px',
                                                            minHeight: '225px',
                                                            margin: '0 auto',
                                                            backgroundColor: '#000',
                                                            borderRadius: '8px',
                                                            overflow: 'hidden',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            {!isCameraReady && (
                                                                <div style={{ position: 'absolute', color: '#fff', zIndex: 1 }}>
                                                                    <div className="spinner-border spinner-border-sm text-light me-2" role="status"></div>
                                                                    Loading camera...
                                                                </div>
                                                            )}
                                                            <video
                                                                ref={videoRef}
                                                                autoPlay
                                                                playsInline
                                                                muted
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    transform: 'scaleX(-1)',
                                                                    display: isCameraReady ? 'block' : 'none'
                                                                }}
                                                            />
                                                        </div>
                                                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                                                        <p className="small text-muted mt-2 mb-2">Position your face in the center</p>
                                                        <div>
                                                        <div className="mt-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                onClick={capturePhoto}
                                                                disabled={!isCameraReady}
                                                            >
                                                                <i className="ri-camera-fill me-2"></i> Capture Photo
                                                            </button>
                                                            <button
                                                                type="button"
                                                              className="btn btn-secondary btn-sm ms-2"
                                                                onClick={stopCamera}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {selfieCaptured && previewImages?.selfie && (
                                                    <div className="text-center">
                                                        <img
                                                            src={previewImages.selfie}
                                                            alt="Captured Selfie"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '200px',
                                                                borderRadius: '8px',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                        <div className="mt-2">
                                                            <h5 className="text-success">✓ Selfie Ready</h5>
                                                            <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary btn-sm"
                                                                    onClick={retakeSelfie}
                                                                >
                                                                    <i className="ri-camera-line me-1"></i> Retake
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => selfieInputRef.current?.click()}
                                                                >
                                                                    <i className="ri-upload-2-line me-1"></i> Upload Different
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                                        <button className="primary_btn prevStep" onClick={(e) => { e.preventDefault(); prevModalStep(); }}>Back</button>
                                        <button className="primary_btn nextStep" onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();

                                            let hasError = false;
                                            const taxNumberNorm = normalizeDocNumber(panCard);
                                            const taxPartial =
                                                Boolean(modalTaxType) ||
                                                Boolean(taxNumberNorm) ||
                                                Boolean(localPanCard);

                                            if (taxPartial && !validateFilledTaxDocumentStep()) {
                                                hasError = true;
                                            }

                                            // Validate selfie
                                            if (!hasError && (!localSelfie || !selfieCaptured)) {
                                                alertErrorMessage('Please capture or upload a selfie');
                                                hasError = true;
                                            }
                                            
                                            // Only proceed if no validation errors
                                            if (!hasError) {
                                                nextModalStep();
                                            }
                                        }}>Next</button>
                                    </div>
                                </div>
                                )}

                                {/* Step 4: Face Verification */}
                                {modalStep === 4 && (
                                <div className="kyc_step text-center active" data-title="Face Verification">
                                    <div className="face_circle" style={{ width: '200px', height: '200px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 20px', border: selfieCaptured && previewImages?.selfie ? '3px solid #28a745' : '3px solid #ccc' }}>
                                        {previewImages?.selfie ? (
                                            <img src={previewImages.selfie} alt="Your Selfie" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <img src="/images/selefvector.png" alt="selfie placeholder" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        )}
                                    </div>
                                    {selfieCaptured && previewImages?.selfie ? (
                                        <>
                                            <h5 className="text-success mb-3">✓ Face Captured Successfully</h5>
                                            <p className="text-muted">Your selfie has been uploaded. Click Next to review your information.</p>
                                        </>
                                    ) : (
                                        <>
                                            <h5 className="text-warning mb-3">Selfie Not Captured</h5>
                                            <p className="text-muted">Please go back and capture your selfie before proceeding.</p>
                                        </>
                                    )}

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                                        <button className="primary_btn prevStep" onClick={(e) => { e.preventDefault(); prevModalStep(); }}>Back</button>
                                        <button className="primary_btn nextStep" onClick={(e) => { 
                                            e.preventDefault(); 
                                            if (!selfieCaptured || !previewImages?.selfie) {
                                                alertErrorMessage('Please go back and capture your selfie first');
                                                return;
                                            }
                                            nextModalStep(); 
                                        }}>Next</button>
                                    </div>
                                </div>
                                )}

                                {/* Step 5: Review Your Information */}
                                {modalStep === 5 && (
                                <div className="kyc_step active" data-title="Review Your Information">
                                    <div className="table-responsive pt-3">
                                        <div className="kyc_information_del">
                                            <div className="userinfolft">
                                                <div className="face_circle" style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', border: '2px solid #28a745' }}>
                                                    {previewImages?.selfie ? (
                                                        <img src={previewImages.selfie} alt="Your Selfie" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src="/images/selefvector.png" alt="selfie" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    )}
                                                </div>
                                                <h5 className="text-dark">{firstName} {lastName}</h5>
                                            </div>

                                            <div className="info_list">
                                                <ul>
                                                    <li>Full Name <span>{firstName} {lastName}</span></li>
                                                    <li>Email <span>{emailId}</span></li>
                                                    <li>Mobile Number <span>{mobileNumber}</span></li>
                                                    <li>Document No. <span>****{aadhar.slice(-4)}</span></li>
                                                    <li>Tax ID <span>{normalizeDocNumber(panCard) ? `****${panCard.slice(-4)}` : '—'}</span></li>
                                                </ul>
                                            </div>

                                        </div>

                                        <div className="documentnumber_s">
                                            <ul>
                                                <li><span>{getIdDocConfig()?.label || 'ID'} Number:</span>****{aadhar.slice(-4)}</li>
                                            </ul>
                                        </div>

                                        <div className="picture_front_bl" style={{ display: 'flex', gap: '20px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                            <div className="document_front_bl" style={{ flex: '1', minWidth: '150px' }}>
                                                <p>Document (Front)</p>
                                                <div className="front_img" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                                                    {previewImages?.doc_front ? (
                                                        <img src={previewImages.doc_front} alt="Document Front" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src="/images/document_front.png" alt="Document Front" style={{ width: '100%' }} />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="document_front_bl" style={{ flex: '1', minWidth: '150px' }}>
                                                <p>Document (Back)</p>
                                                <div className="front_img" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                                                    {previewImages?.doc_back ? (
                                                        <img src={previewImages.doc_back} alt="Document Back" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src="/images/document_front.png" alt="Document Back" style={{ width: '100%' }} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="documentnumber_s mt-3">
                                            <ul>
                                                <li><span>{getTaxDocConfig()?.label || 'Tax ID'}:</span>{normalizeDocNumber(panCard) ? `****${panCard.slice(-4)}` : '—'}</li>
                                            </ul>
                                        </div>

                                        <div className="picture_front_bl" style={{ display: 'flex', gap: '20px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                            <div className="document_front_bl" style={{ flex: '1', minWidth: '150px' }}>
                                                <p>Tax Document</p>
                                                <div className="front_img" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                                                    {previewImages?.pan ? (
                                                        <img src={previewImages.pan} alt="Tax Document" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src="/images/document_front.png" alt="Tax Document" style={{ width: '100%' }} />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="document_front_bl" style={{ flex: '1', minWidth: '150px' }}>
                                                <p>Selfie</p>
                                                <div className="front_img" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                                                    {previewImages?.selfie ? (
                                                        <img src={previewImages.selfie} alt="Selfie" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src="/images/document_front.png" alt="Selfie" style={{ width: '100%' }} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                                        <button type="button" className="primary_btn prevStep" onClick={(e) => { e.preventDefault(); prevModalStep(); }}>Back</button>
                                        <button type="button" className="primary_btn nextStep" onClick={(e) => {
                                            e.preventDefault();
                                            handleModalKycSubmit();
                                        }}>Submit KYC</button>
                                    </div>
                                </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                {/* <!-- Modal kyc End --> */}

                {/* === RESUBMIT MODAL === */}
                {/* This modal only shows rejected documents for resubmission - SAME DESIGN AS MAIN KYC MODAL */}
                <div className="modal fade kyc_modal" id="kycResubmitModal" tabIndex="-1" data-bs-backdrop="static">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title" id="kycResubmitTitle">
                                    {resubmitStep === 0 ? 'Resubmit Documents' :
                                        resubmitStep === 1 && needsIdDocResubmit() ? `Upload ${submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'ID Document'}` :
                                            resubmitStep === 2 && needsTaxDocResubmit() ? `Upload ${submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'}` :
                                                resubmitStep === 3 && needsSelfieResubmit() ? 'Capture Selfie' : 'Resubmit Documents'}
                                </h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                            </div>

                            <div className="modal-body">

                                {/* Step 0: Documents Requiring Resubmission */}
                                {resubmitStep === 0 && (
                                    <div className="kyc_step active" data-title="Resubmit Documents">
                                        <label className="label">The following documents were rejected and need to be uploaded again:</label>

                                        <div className="alert alert-danger mt-3">
                                            <ul className="mb-0" style={{ listStyle: 'none', paddingLeft: 0 }}>
                                                {needsIdDocResubmit() && (
                                                    <li className="mb-2">
                                                        <strong><i className="ri-close-circle-line me-2"></i>{submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'ID Document'}:</strong> {getRejectReason('id_document')}
                                                    </li>
                                                )}
                                                {needsTaxDocResubmit() && (
                                                    <li className="mb-2">
                                                        <strong><i className="ri-close-circle-line me-2"></i>{submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'}:</strong> {getRejectReason('tax_document')}
                                                    </li>
                                                )}
                                                {needsSelfieResubmit() && (
                                                    <li className="mb-2">
                                                        <strong><i className="ri-close-circle-line me-2"></i>Selfie:</strong> {getRejectReason('selfie')}
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        <button type="button" className="primary_btn nextStep" onClick={() => setResubmitStep(1)}>Continue</button>
                                    </div>
                                )}

                                {/* Step 1: ID Document Resubmission (if needed) */}
                                {resubmitStep === 1 && needsIdDocResubmit() && (
                                    <div className="kyc_step active" data-title={`Upload ${submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'ID Document'}`}>
                                        {/* Rejection Reason Alert */}
                                        <div className="alert alert-warning mb-3">
                                            <strong>Rejection Reason:</strong> {getRejectReason('id_document')}
                                        </div>

                                        {/* ID Preview Image - same as original */}
                                        <div className="id_preview">
                                            <img src="/images/photoid_vector.png" alt="ID card" />
                                        </div>

                                        {/* Photo Tips - same as original */}
                                        <div className="tips photomini">
                                            <p><img src="/images/photoidmini.png" alt="tip 1" /></p>
                                            <p><img src="/images/photoidmini2.png" alt="tip 2" /></p>
                                            <p><img src="/images/photoidmini3.png" alt="tip 3" /></p>
                                        </div>

                                        <h6>Your previously selected ID type:</h6>

                                        {/* Country and ID Type Info */}
                                        <div className="info_text">
                                            <ul className="d-flex gap-3">
                                                <li>
                                                    {countries.find(c => c.code === existingCountryCode)?.flag || '🌍'}
                                                    {' '}{countries.find(c => c.code === existingCountryCode)?.name || 'Your Country'}
                                                </li>
                                                <li><img src="/images/idcard.png" alt="ID Card" /> {submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'ID Document'}</li>
                                            </ul>
                                        </div>
                                        <p>Please upload a clear, valid ID document to avoid further rejection.</p>

                                        {/* Document Number Input */}
                                        <input
                                            className="input"
                                            placeholder={`${submittedIdDocType ? getDocTypeName(submittedIdDocType) : 'ID Document'} Number`}
                                            value={resubmitIdNumber}
                                            onChange={(e) => setResubmitIdNumber(e.target.value.toUpperCase())}
                                        />
                                        <small className="text-muted d-block mb-3">You can update the number if it was incorrect</small>

                                        {/* Upload Grid - same as original */}
                                        <div className="upload_grid">
                                            <div className="upload-box">
                                                <input type="file" id="resubmitDocFront" hidden accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleFileChange(e, 'id_front')} />
                                                <label htmlFor="resubmitDocFront" className="upload-label">
                                                    {previewImages?.doc_front ? (
                                                        <img src={previewImages.doc_front} alt="Document Front" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img className="upload_back_img" src="/images/fileback_vector.png" alt="upload background" />
                                                    )}
                                                    <div className="icon">
                                                        <img src="/images/uploadvector.svg" alt="upload" />
                                                    </div>
                                                    <h3>{localFront ? '✓ Uploaded' : 'Front Side'}</h3>
                                                    <p>{localFront?.name || 'Drag or choose file'}</p>
                                                </label>
                                            </div>

                                            <div className="upload-box">
                                                <input type="file" id="resubmitDocBack" hidden accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleFileChange(e, 'id_back')} />
                                                <label htmlFor="resubmitDocBack" className="upload-label">
                                                    {previewImages?.doc_back ? (
                                                        <img src={previewImages.doc_back} alt="Document Back" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img className="upload_back_img" src="/images/fileback_vector.png" alt="upload background" />
                                                    )}
                                                    <div className="icon">
                                                        <img src="/images/uploadvector.svg" alt="upload" />
                                                    </div>
                                                    <h3>{localBack ? '✓ Uploaded' : 'Back Side'} {getIdDocConfig()?.requires_back_image ? '' : '(Optional)'} </h3>
                                                    <p>{localBack?.name || 'Drag or choose file'}</p>
                                                </label>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                                            <button className="primary_btn prevStep" onClick={() => setResubmitStep(0)}>Back</button>
                                            <button className="primary_btn nextStep" onClick={() => {
                                                const idValidation = validateDocNumber(resubmitIdNumber);
                                                if (!idValidation.valid) {
                                                    alertErrorMessage(idValidation.message || 'Please enter a valid document number');
                                                    return;
                                                }
                                                if (!localFront) { alertErrorMessage('Please upload front image of your ID card'); return; }
                                                if (needsTaxDocResubmit()) setResubmitStep(2);
                                                else if (needsSelfieResubmit()) setResubmitStep(3);
                                                else handleResubmitKyc();
                                            }}>{(needsTaxDocResubmit() || needsSelfieResubmit()) ? 'Next' : (isSubmitting ? 'Submitting...' : 'Submit')}</button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 1 (Skip ID): Auto-advance if no ID resubmit needed */}
                                {resubmitStep === 1 && !needsIdDocResubmit() && (
                                    <div className="kyc_step active">
                                        {(() => {
                                            setTimeout(() => {
                                                if (needsTaxDocResubmit()) setResubmitStep(2);
                                                else if (needsSelfieResubmit()) setResubmitStep(3);
                                                else handleResubmitKyc();
                                            }, 0); return null;
                                        })()}
                                        <p className="text-center">Loading...</p>
                                    </div>
                                )}

                                {/* Step 2: Tax Document Resubmission (if needed) */}
                                {resubmitStep === 2 && needsTaxDocResubmit() && (
                                    <div className="kyc_step active" data-title={`Upload ${submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'}`}>
                                        {/* Rejection Reason Alert */}
                                        <div className="alert alert-warning mb-3">
                                            <strong>Rejection Reason:</strong> {getRejectReason('tax_document')}
                                        </div>

                                        <div className="d-flex gap-4 flex-column">
                                            {/* Tax Document Info */}
                                            <div>
                                                <h6>Your previously selected Tax Document type:</h6>
                                                <div className="info_text mb-3">
                                                    <ul className="d-flex gap-3">
                                                        <li>
                                                            {countries.find(c => c.code === existingCountryCode)?.flag || '🌍'}
                                                            {' '}{countries.find(c => c.code === existingCountryCode)?.name || 'Your Country'}
                                                        </li>
                                                        <li><img src="/images/idcard.png" alt="Tax Doc" /> {submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'}</li>
                                                    </ul>
                                                </div>

                                                {/* Document Number Input */}
                                                <label className="label mb-2">
                                                    {submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax ID'} Number <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    className="input"
                                                    placeholder={`Enter ${submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax ID'} Number`}
                                                    value={resubmitTaxNumber}
                                                    onChange={(e) => setResubmitTaxNumber(e.target.value.toUpperCase())}
                                                />
                                                <small className="text-muted d-block mb-3">You can update the number if it was incorrect</small>
                                            </div>

                                            {/* Upload Section */}
                                            <div>
                                                <h5 className="mb-2">Upload {submittedTaxDocType ? getDocTypeName(submittedTaxDocType) : 'Tax Document'} <span className="text-danger">*</span></h5>
                                                <span className="small text-muted">(Only JPEG, PNG & JPG formats and file size upto 5MB are supported)</span>
                                                <div className="upload-box mt-2">
                                                    <input type="file" id="resubmitPanCard" hidden accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleFileChange(e, 'pan')} />
                                                    <label htmlFor="resubmitPanCard" className="upload-label">
                                                        {previewImages?.pan ? (
                                                            <img src={previewImages.pan} alt="Tax Document" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                                                        ) : (
                                                            <div className="icon">
                                                                <img src="/images/uploadvector.svg" alt="upload" />
                                                            </div>
                                                        )}
                                                        <h3>{localPanCard ? '✓ Uploaded' : 'Choose a File'}</h3>
                                                        <p>{localPanCard?.name || 'Drag or choose your file to upload'}</p>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                                            <button className="primary_btn prevStep" onClick={() => {
                                                if (needsIdDocResubmit()) setResubmitStep(1);
                                                else setResubmitStep(0);
                                            }}>Back</button>
                                            <button className="primary_btn nextStep" onClick={() => {
                                                const taxValidation = validateDocNumber(resubmitTaxNumber);
                                                if (!taxValidation.valid) {
                                                    alertErrorMessage(taxValidation.message || 'Please enter a valid Tax ID');
                                                    return;
                                                }
                                                if (!localPanCard) { alertErrorMessage('Please upload Tax document'); return; }
                                                if (needsSelfieResubmit()) setResubmitStep(3);
                                                else handleResubmitKyc();
                                            }}>{needsSelfieResubmit() ? 'Next' : (isSubmitting ? 'Submitting...' : 'Submit')}</button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2 (Skip Tax): Auto-advance if no Tax resubmit needed */}
                                {resubmitStep === 2 && !needsTaxDocResubmit() && (
                                    <div className="kyc_step active">
                                        {(() => {
                                            setTimeout(() => {
                                                if (needsSelfieResubmit()) setResubmitStep(3);
                                                else handleResubmitKyc();
                                            }, 0); return null;
                                        })()}
                                        <p className="text-center">Loading...</p>
                                    </div>
                                )}

                                {/* Step 3: Selfie Resubmission (if needed) */}
                                {resubmitStep === 3 && needsSelfieResubmit() && (
                                    <div className="kyc_step active" data-title="Capture Selfie">
                                        <div className="alert alert-warning mb-3">
                                            <strong>Rejection Reason:</strong> {getRejectReason('selfie')}
                                        </div>

                                        <h5 className="mb-2 text-dark">Selfie <span className="text-danger">*</span></h5>
                                        <span className="small text-muted">(Use your camera to capture live, or upload a photo from your device)</span>

                                        <input
                                            type="file"
                                            ref={selfieInputRef}
                                            hidden
                                            accept="image/png,image/jpeg,image/jpg"
                                            onChange={(e) => handleFileChange(e, 'selfie')}
                                        />

                                        <div className="upload-box mt-2" style={{ minHeight: '200px' }}>
                                            {cameraError && (
                                                <div className="alert alert-danger text-center p-2 mb-2">
                                                    {cameraError}
                                                </div>
                                            )}

                                            {!isCameraActive && !selfieCaptured && (
                                                <div className="text-center p-3">
                                                    <div className="selfie_circle mb-3" style={{ margin: '0 auto' }}>
                                                        <img src="/images/selefvector.png" alt="selfie guide" />
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={startCamera}
                                                        >
                                                            <i className="ri-camera-line me-2"></i> Start Camera
                                                        </button>
                                                        <span className="text-muted">or</span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary"
                                                            onClick={() => selfieInputRef.current?.click()}
                                                        >
                                                            <i className="ri-upload-2-line me-2"></i> Upload Photo
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {isCameraActive && !selfieCaptured && (
                                                <div className="text-center">
                                                    <div style={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        minHeight: '225px',
                                                        margin: '0 auto',
                                                        backgroundColor: '#000',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {!isCameraReady && (
                                                            <div style={{ position: 'absolute', color: '#fff', zIndex: 1 }}>
                                                                <div className="spinner-border spinner-border-sm text-light me-2" role="status"></div>
                                                                Loading camera...
                                                            </div>
                                                        )}
                                                        <video
                                                            ref={videoRef}
                                                            autoPlay
                                                            playsInline
                                                            muted
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                transform: 'scaleX(-1)',
                                                                display: isCameraReady ? 'block' : 'none'
                                                            }}
                                                        />
                                                    </div>
                                                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                                                    <p className="small text-muted mt-2 mb-2">Position your face in the center</p>
                                                    <div className="mt-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-success"
                                                            onClick={capturePhoto}
                                                            disabled={!isCameraReady}
                                                        >
                                                            <i className="ri-camera-fill me-2"></i> Capture Photo
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary ms-2"
                                                            onClick={stopCamera}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {selfieCaptured && previewImages?.selfie && (
                                                <div className="text-center">
                                                    <img
                                                        src={previewImages.selfie}
                                                        alt="Captured Selfie"
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '200px',
                                                            borderRadius: '8px',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                    <div className="mt-2">
                                                        <h5 className="text-success">✓ Selfie Ready</h5>
                                                        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={retakeSelfie}
                                                            >
                                                                <i className="ri-camera-line me-1"></i> Retake
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => selfieInputRef.current?.click()}
                                                            >
                                                                <i className="ri-upload-2-line me-1"></i> Upload Different
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
                                            <button className="primary_btn prevStep" onClick={() => {
                                                stopCamera();
                                                if (needsTaxDocResubmit()) setResubmitStep(2);
                                                else if (needsIdDocResubmit()) setResubmitStep(1);
                                                else setResubmitStep(0);
                                            }}>Back</button>
                                            <button className="primary_btn nextStep" onClick={() => {
                                                if (!localSelfie || !selfieCaptured) { alertErrorMessage('Please capture or upload a selfie'); return; }
                                                stopCamera();
                                                handleResubmitKyc();
                                            }}>{isSubmitting ? 'Submitting...' : 'Submit'}</button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 3 (Skip Selfie): Auto-advance if no Selfie resubmit needed */}
                                {resubmitStep === 3 && !needsSelfieResubmit() && (
                                    <div className="kyc_step active">
                                        {(() => { setTimeout(() => handleResubmitKyc(), 0); return null; })()}
                                        <p className="text-center">Submitting...</p>
                                    </div>
                                )}

                                {/* OTP verification step removed — resubmit submits directly */}

                            </div>
                        </div>
                    </div>
                </div>
                {/* === END RESUBMIT MODAL === */}

                {/* KYC Submit Confirmation Modal */}
                <div className="modal fade" id="kycSubmitModal" tabIndex="-1" aria-labelledby="kycSubmitModalLabel" aria-hidden="true" data-bs-backdrop="static">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content kyc_modal">
                            <div className="modal-header">
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div className="modal-body text-center">
                                <form className="profile_form" >
                                <div className="mb-4">
                                    <div className="success_icon_wrapper mb-3">
                                        <img src="/images/verifing_vector.png" alt="Verifying" />
                                    </div>
                                    <h4 className="mb-3">Verifying</h4>
                                    <p className="text-muted mb-2">
                                        Hang tight, your review will be completed within the next 48 hours.
                                    </p>
                                    <p className="text-muted mb-2">Continue exploring Exchange while you wait. We'll notify you once
                                        verification is complete.</p>
                                </div>
                                <div className="d-flex gap-3 justify-content-center ">
                                    <button type="button" className="primary_btn submit"  data-bs-dismiss="modal" >
                                        Done
                                    </button>
                                </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </>
    );
};

export default KycPage;
